/**
 * Dashboard Page Component - Robinhood-Inspired Design
 * 
 * Modern, clean dashboard interface focusing on portfolio performance and trading activity.
 * Features a hero section with portfolio value, P&L metrics, and trading activity.
 * 
 * Key features:
 * - Portfolio hero with large value display and performance metrics
 * - P&L cards showing Today's P&L, All-Time P&L, and Today's Winning Trade
 * - Recent trading activity feed
 * - Consolidated holdings across all accounts
 * - Auto-refresh and loading states
 * - Dark/light mode support
 * - Mobile-responsive design
 */

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTradingQuery } from '../hooks/useTradingQuery';
import type { Account } from '@trading-app/shared';
import { accountsService } from '../services/accounts';
import { tradingService } from '../services/trading';
import { ProfitOverTimeChart } from '../components/analytics/ProfitOverTimeChart';
import { RecentActivity } from '../components/trading/RecentActivity';
import { HoldingsTable } from '../components/holdings/HoldingsTable';
import { CircuitBreakerBar } from '../components/risk/CircuitBreakerBar';
import { ProfitTargetIndicator } from '../components/risk/ProfitTargetIndicator';
import { RegimeBadge } from '../components/risk/RegimeBadge';
import { ModeStandingsWidget } from '../components/ModeStandingsWidget';
import { OpenPositionBanner } from '../components/OpenPositionBanner';

// Helper function to calculate date range based on period
function getDateRange(period: string): { startDate: string; endDate: string; granularity: 'hour' | 'day' } {
  const now = new Date();
  const endDate = now.toISOString();
  let startDate: Date;
  let granularity: 'hour' | 'day' = 'hour';

  switch (period) {
    case '1d':
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      granularity = 'hour';
      break;
    case '1w':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      granularity = 'hour';
      break;
    case '1m':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      granularity = 'day';
      break;
    case '3m':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      granularity = 'day';
      break;
    case 'ytd':
      // Year to date - January 1st of current year
      startDate = new Date(now.getFullYear(), 0, 1);
      granularity = 'day';
      break;
    case '1y':
      startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      granularity = 'day';
      break;
    case 'all':
      startDate = new Date(now.getTime() - 5 * 365 * 24 * 60 * 60 * 1000); // 5 years back
      granularity = 'day';
      break;
    default:
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      granularity = 'hour';
  }

  return {
    startDate: startDate.toISOString(),
    endDate,
    granularity,
  };
}

/**
 * Check if the US stock market is currently open.
 * Market hours: 6:30 AM - 1:00 PM PT (9:30 AM - 4:00 PM ET), Monday-Friday
 * 
 * When market is open, we show "Today" data even if empty (no fallback to last trading day).
 * When market is closed, we fall back to the last trading day's data.
 */
function isMarketOpen(): boolean {
  const now = new Date();
  
  // Get Pacific timezone weekday separately to avoid locale formatting issues
  const dayOfWeek = now.toLocaleDateString('en-US', { 
    timeZone: 'America/Los_Angeles',
    weekday: 'short'
  }); // e.g., "Mon", "Tue", etc.
  
  // Get hours and minutes separately for reliable parsing
  const hours = parseInt(now.toLocaleString('en-US', { 
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    hour12: false
  }), 10);
  
  const minutes = parseInt(now.toLocaleString('en-US', { 
    timeZone: 'America/Los_Angeles',
    minute: 'numeric'
  }), 10);
  
  const currentMinutes = hours * 60 + minutes;
  
  // Weekend - market closed
  if (dayOfWeek === 'Sat' || dayOfWeek === 'Sun') {
    return false;
  }
  
  // Market hours: 6:30 AM - 1:00 PM PT
  const marketOpenMinutes = 6 * 60 + 30;  // 6:30 AM = 390 minutes
  const marketCloseMinutes = 13 * 60;     // 1:00 PM = 780 minutes
  
  return currentMinutes >= marketOpenMinutes && currentMinutes < marketCloseMinutes;
}

export function Dashboard() {
  const { user, logout } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('1d');
  
  // Query for accounts
  const {
    data: accounts = [],
    isLoading: isLoadingAccounts,
  } = useTradingQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsService.getAccounts(),
    retry: 2,
  });

  // Map selected period to time range for trade aggregates API
  const timeRangeMapping: Record<string, 'today' | 'week' | 'month' | 'year' | 'all'> = {
    '1d': 'today',
    '1w': 'week',
    '1m': 'month',
    '3m': 'all', // No direct mapping, use 'all' and filter
    'ytd': 'year',
    '1y': 'year',
    'all': 'all',
  };

  // Map time period buttons to timeRange
  const timeRangeForChart: 'today' | 'week' | 'month' | 'year' | 'all' =
    timeRangeMapping[selectedPeriod] || 'all';

  // Query for trade history (for RecentActivity component)
  const {
    data: trades = [],
    isLoading: isLoadingTrades,
  } = useTradingQuery({
    queryKey: ['tradeHistory'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeHistory();
      } catch (err) {
        console.error('Trade history fetch error:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Calculate portfolio metrics
  const totalValue = accounts.reduce((sum, account) => sum + account.balance, 0);

  // Query for trade aggregates (for All-Time P&L from closed trades)
  // Request all records (no limit) for accurate Avg Profit Goals calculation
  const {
    data: tradeAggregates = [],
    isLoading: isLoadingAggregates,
  } = useTradingQuery({
    queryKey: ['tradeAggregates', 'all'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeAggregates({
          timeRange: 'all',
          limit: 10000, // Request all trades for accurate daily average calculation
        });
      } catch (err) {
        console.error('Trade aggregates fetch error:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Query for today's closed trade aggregates (for Today's P&L metrics)
  // Uses the same endpoint as RecentActivity to ensure consistency
  const {
    data: todayTradeAggregates = [],
    isLoading: isLoadingTodayAggregates,
  } = useTradingQuery({
    queryKey: ['tradeAggregates', 'today', 'closed'],
    queryFn: () => tradingService.getTodayClosedAggregates(),
    retry: 2,
  });

  // Query for open position aggregates (single source of truth for holdings)
  const {
    data: openAggregates = [],
    isLoading: isLoadingOpenAggregates,
  } = useTradingQuery({
    queryKey: ['tradeAggregates', 'open'],
    queryFn: async () => {
      try {
        return await tradingService.getOpenAggregates();
      } catch (err) {
        console.error('Open aggregates fetch error:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Query for selected period's trade aggregates (for border color and Avg Daily Profit)
  const {
    data: selectedPeriodAggregates = [],
    isLoading: isLoadingSelectedPeriod,
  } = useTradingQuery({
    queryKey: ['tradeAggregates', timeRangeForChart],
    queryFn: async () => {
      try {
        return await tradingService.getTradeAggregates({
          timeRange: timeRangeForChart,
          limit: 10000, // Request all trades for accurate calculations
        });
      } catch (err) {
        console.error('Selected period aggregates fetch error:', err);
        return [];
      }
    },
    retry: 2,
    enabled: timeRangeForChart !== 'all', // Use existing all-time data
  });

  // Query for algorithm aggregate stats (for Algorithm Performance metrics)
  const {
    data: algorithmMetrics = [],
    isLoading: isLoadingAlgorithmMetrics,
  } = useTradingQuery({
    queryKey: ['aggregateStats', '', '', '', timeRangeForChart],
    queryFn: async () => {
      try {
        return await tradingService.getAggregateStats(undefined, undefined, undefined, timeRangeForChart);
      } catch (err) {
        console.error('Aggregate stats fetch error:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Query for algorithm configs (to get circuit breaker config ID)
  const {
    data: algorithmConfigs = [],
  } = useTradingQuery({
    queryKey: ['algorithmConfigs'],
    queryFn: async () => {
      try {
        return await tradingService.getAlgorithmConfigs();
      } catch (err) {
        console.error('Algorithm configs fetch error:', err);
        return [];
      }
    },
    retry: 2,
  });

  // Get active algorithm config for circuit breaker
  const activeConfig = algorithmConfigs.find(c => c.isActive);

  // Query for circuit breaker status (only if we have an active config)
  const {
    data: circuitBreakerStatus,
    isLoading: isLoadingCircuitBreaker,
  } = useTradingQuery({
    queryKey: ['circuitBreakerStatus', activeConfig?.id],
    queryFn: async () => {
      if (!activeConfig) return null;
      try {
        return await tradingService.getCircuitBreakerStatus(activeConfig.id);
      } catch (err) {
        console.error('Circuit breaker status fetch error:', err);
        return null;
      }
    },
    retry: 2,
    refetchInterval: 10000, // Refresh every 10 seconds (overrides default 30s)
    enabled: !!activeConfig, // Only fetch if we have an active config
  });

  // Query for current regime status
  const {
    data: regimeData,
    isLoading: isLoadingRegime,
  } = useTradingQuery({
    queryKey: ['currentRegime'],
    queryFn: async () => {
      try {
        return await tradingService.getCurrentRegime();
      } catch (err) {
        console.error('Regime fetch error:', err);
        return null;
      }
    },
    refetchInterval: 60000, // Refresh every 60s (regime only changes daily)
  });

  // Query for regime history (for chart shading)
  const {
    data: regimeHistory,
  } = useTradingQuery({
    queryKey: ['regimeHistory'],
    queryFn: async () => {
      try {
        const result = await tradingService.getRegimeHistory();
        return result.regimes;
      } catch (err) {
        console.error('Regime history fetch error:', err);
        return [];
      }
    },
    refetchInterval: 60000,
  });

  // Query for regime P&L (profit per regime zone)
  const {
    data: regimePnL,
  } = useTradingQuery({
    queryKey: ['regimePnL'],
    queryFn: async () => {
      try {
        const result = await tradingService.getRegimePnL();
        return result.periods;
      } catch (err) {
        console.error('Regime P&L fetch error:', err);
        return [];
      }
    },
    refetchInterval: 60000,
  });

  // Determine if we need to fall back to the last trading day
  // This must be computed before algorithmPerformance to support fallback
  const lastTradingDayInfo = useMemo(() => {
    const hasTodayTrades = todayTradeAggregates.length > 0;
    const marketOpen = isMarketOpen();
    
    // Don't use fallback if:
    // 1. Today's data is still loading - wait for it
    // 2. Today has trades - show today's data
    // 3. Market is currently open - show empty "Today" rather than stale data
    if (hasTodayTrades || tradeAggregates.length === 0 || isLoadingTodayAggregates || marketOpen) {
      return {
        shouldFallback: false,
        lastTradingDate: null,
        lastTradingDateLabel: 'Today',
        lastTradingDayAggregates: todayTradeAggregates,
      };
    }

    // Find the most recent trading day from all aggregates
    // Group by date in Pacific timezone and find the most recent
    const dateMap = new Map<string, { closeDate: Date; aggregates: typeof tradeAggregates }>();
    
    tradeAggregates.forEach(agg => {
      if (agg.closeTime) {
        const closeDate = new Date(agg.closeTime);
        // Get Pacific date key in YYYY-MM-DD format for sorting
        const dateKey = closeDate.toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { closeDate, aggregates: [] });
        }
        dateMap.get(dateKey)!.aggregates.push(agg);
      }
    });

    // Get sorted dates and find the most recent
    const sortedDates = Array.from(dateMap.keys()).sort().reverse();
    
    if (sortedDates.length === 0) {
      return {
        shouldFallback: false,
        lastTradingDate: null,
        lastTradingDateLabel: 'Today',
        lastTradingDayAggregates: [],
      };
    }

    const lastDateKey = sortedDates[0];
    const lastDateInfo = dateMap.get(lastDateKey)!;
    
    // Use an actual closeTime from the last trading day to format the label
    // This ensures we have a proper UTC timestamp to convert to Pacific for display
    const dateLabel = lastDateInfo.closeDate.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    return {
      shouldFallback: true,
      lastTradingDate: lastDateInfo.closeDate,
      lastTradingDateLabel: dateLabel,
      lastTradingDayAggregates: lastDateInfo.aggregates,
    };
  }, [todayTradeAggregates, tradeAggregates, isLoadingTodayAggregates]);

  // Get effective today aggregates (with fallback to last trading day)
  // Extracted for use by both pnlMetrics and algorithmPerformance
  const effectiveTodayAggregates = useMemo(() => {
    return lastTradingDayInfo.shouldFallback 
      ? lastTradingDayInfo.lastTradingDayAggregates 
      : todayTradeAggregates;
  }, [lastTradingDayInfo, todayTradeAggregates]);

  // Helper function to calculate average trade duration from aggregates
  const calculateAvgDuration = (aggregates: typeof tradeAggregates) => {
    const closedTrades = aggregates.filter(agg => agg.openTime && agg.closeTime);
    if (closedTrades.length === 0) return null;
    
    const totalDurationMs = closedTrades.reduce((sum, agg) => {
      const open = new Date(agg.openTime!).getTime();
      const close = new Date(agg.closeTime!).getTime();
      return sum + (close - open);
    }, 0);
    
    const avgMs = totalDurationMs / closedTrades.length;
    const totalSeconds = Math.floor(avgMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Helper function to calculate total buy/sell volumes from aggregates
  const calculateVolumes = (aggregates: typeof tradeAggregates) => {
    const totalBuyVolume = aggregates.reduce((sum, agg) => sum + (agg.buyNotionalTotal || 0), 0);
    const totalSellVolume = aggregates.reduce((sum, agg) => sum + (agg.sellNotionalTotal || 0), 0);
    return { totalBuyVolume, totalSellVolume };
  };

  // Calculate overall algorithm performance metrics
  // Uses fallback data when viewing 'today' on non-trading days/times
  const algorithmPerformance = useMemo(() => {
    // When viewing 'today' and fallback is needed, compute from effectiveTodayAggregates
    if (timeRangeForChart === 'today' && lastTradingDayInfo.shouldFallback) {
      const aggregates = effectiveTodayAggregates;
      const totalTrades = aggregates.length;
      const totalPnL = aggregates.reduce((sum, agg) => sum + agg.netPnl, 0);
      const wins = aggregates.filter((agg) => agg.netPnl > 0).length;
      const losses = aggregates.filter((agg) => agg.netPnl < 0).length;
      const totalCompletedTrades = wins + losses;
      const avgDuration = calculateAvgDuration(aggregates);
      const { totalBuyVolume, totalSellVolume } = calculateVolumes(aggregates);

      return {
        totalTrades,
        totalPnL,
        wins,
        losses,
        winRate: totalCompletedTrades > 0 ? (wins / totalCompletedTrades) * 100 : 0,
        avgDuration,
        totalBuyVolume,
        totalSellVolume,
      };
    }

    // Otherwise use algorithmMetrics from API
    const total = algorithmMetrics.reduce(
      (acc, metrics) => ({
        totalTrades: acc.totalTrades + metrics.totalTrades,
        totalPnL: acc.totalPnL + metrics.totalPnL,
        wins: acc.wins + metrics.wins,
        losses: acc.losses + metrics.losses,
      }),
      { totalTrades: 0, totalPnL: 0, wins: 0, losses: 0 }
    );

    const totalCompletedTrades = total.wins + total.losses;
    
    // For non-fallback cases, calculate duration and volumes from the appropriate data source
    const aggregatesForMetrics = timeRangeForChart === 'all' 
      ? tradeAggregates 
      : timeRangeForChart === 'today' 
        ? todayTradeAggregates 
        : selectedPeriodAggregates;
    const avgDuration = calculateAvgDuration(aggregatesForMetrics);
    const { totalBuyVolume, totalSellVolume } = calculateVolumes(aggregatesForMetrics);

    return {
      ...total,
      winRate: totalCompletedTrades > 0 ? (total.wins / totalCompletedTrades) * 100 : 0,
      avgDuration,
      totalBuyVolume,
      totalSellVolume,
    };
  }, [algorithmMetrics, timeRangeForChart, lastTradingDayInfo, effectiveTodayAggregates, tradeAggregates, todayTradeAggregates, selectedPeriodAggregates]);

  // Calculate P&L metrics
  const pnlMetrics = useMemo(() => {
    // All-time P&L - use trade_aggregates (only closed trades with realized P&L)
    const allTimePnL = tradeAggregates.reduce((sum, agg) => sum + agg.netPnl, 0);

    // Today's P&L - use today's trade_aggregates (closed trades only)
    const todayPnL = effectiveTodayAggregates.reduce((sum, agg) => sum + agg.netPnl, 0);

    // Select data source for best/worst calculations based on selected time range
    const aggregatesForBestWorst = timeRangeForChart === 'all' 
      ? tradeAggregates 
      : timeRangeForChart === 'today' 
        ? effectiveTodayAggregates 
        : selectedPeriodAggregates;

    // Initialize all four metrics
    let bestTrade: { ticker: string; pnl: number; pnlPercent: number } | null = null;
    let worstTrade: { ticker: string; pnl: number; pnlPercent: number } | null = null;
    let bestStock: { ticker: string; pnl: number; pnlPercent: number } | null = null;
    let worstStock: { ticker: string; pnl: number; pnlPercent: number } | null = null;

    if (aggregatesForBestWorst.length > 0) {
      // Find the single best and worst individual trades
      let highestPnL = -Infinity;
      let lowestPnL = Infinity;

      aggregatesForBestWorst.forEach((agg: any) => {
        const pnlPercent = agg.buyNotionalTotal > 0
          ? ((agg.sellNotionalTotal - agg.buyNotionalTotal) / agg.buyNotionalTotal) * 100
          : 0;

        // Best individual trade (highest P&L)
        if (agg.netPnl > highestPnL) {
          highestPnL = agg.netPnl;
          bestTrade = {
            ticker: agg.ticker,
            pnl: agg.netPnl,
            pnlPercent,
          };
        }

        // Worst individual trade (lowest P&L)
        if (agg.netPnl < lowestPnL) {
          lowestPnL = agg.netPnl;
          worstTrade = {
            ticker: agg.ticker,
            pnl: agg.netPnl,
            pnlPercent,
          };
        }
      });

      // Group aggregates by ticker and sum their P&L for best/worst stock
      const tickerMap = new Map<string, { netPnl: number; buyNotional: number; sellNotional: number }>();

      aggregatesForBestWorst.forEach((agg: any) => {
        const ticker = agg.ticker;
        if (!tickerMap.has(ticker)) {
          tickerMap.set(ticker, { netPnl: 0, buyNotional: 0, sellNotional: 0 });
        }
        const tickerData = tickerMap.get(ticker)!;
        tickerData.netPnl += agg.netPnl;
        tickerData.buyNotional += agg.buyNotionalTotal;
        tickerData.sellNotional += agg.sellNotionalTotal;
      });

      // Find best and worst performing stocks (by total P&L)
      let bestStockPnL = -Infinity;
      let worstStockPnL = Infinity;

      tickerMap.forEach((data, ticker) => {
        const pnlPercent = data.buyNotional > 0
          ? ((data.sellNotional - data.buyNotional) / data.buyNotional) * 100
          : 0;

        // Best stock (highest total P&L)
        if (data.netPnl > bestStockPnL) {
          bestStockPnL = data.netPnl;
          bestStock = {
            ticker,
            pnl: data.netPnl,
            pnlPercent,
          };
        }

        // Worst stock (lowest total P&L)
        if (data.netPnl < worstStockPnL) {
          worstStockPnL = data.netPnl;
          worstStock = {
            ticker,
            pnl: data.netPnl,
            pnlPercent,
          };
        }
      });
    }

    // Select data source based on selected time range
    // For 'today', use effectiveTodayAggregates to include fallback to last trading day
    // when it's not a trading day or trading hours
    const aggregatesForAverage = timeRangeForChart === 'all' 
      ? tradeAggregates 
      : timeRangeForChart === 'today' 
        ? effectiveTodayAggregates 
        : selectedPeriodAggregates;

    // Calculate Daily Profit Running Average (PST/PDT - Pacific Time)
    // This shows the average daily P&L from closed trade pairs for the selected time period
    let dailyAverage: {
      average: number;
      tradingDays: number;
      trend: 'up' | 'down' | null;
    } = { average: 0, tradingDays: 0, trend: null };

    if (aggregatesForAverage.length > 0) {
      // Get today's date in Pacific timezone (YYYY-MM-DD format)
      const todayKey = new Date().toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles'
      });
      
      // Check if market is closed (after 1pm PT / 4pm ET)
      const pacificHour = parseInt(
        new Date().toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
          hour: 'numeric',
          hour12: false
        })
      );
      const isMarketClosed = pacificHour >= 13; // 1pm PT = 4pm ET

      // Group closed trades by date (Pacific timezone)
      const tradeDayMap = new Map<string, number>();
      
      aggregatesForAverage.forEach((agg: any) => {
        if (agg.closeTime) {
          // Get date in Pacific timezone (YYYY-MM-DD format)
          const dateKey = new Date(agg.closeTime).toLocaleDateString('en-CA', {
            timeZone: 'America/Los_Angeles'
          });
          
          // Get day of week in Pacific timezone
          const pacificDay = new Date(agg.closeTime).toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
            weekday: 'short'
          });
          const isWeekday = !['Sat', 'Sun'].includes(pacificDay);
          
          // Include weekdays (Mon-Fri)
          // Include today only if market is closed OR if specifically viewing today's data
          const isToday = dateKey === todayKey;
          const includeTodayRegardless = timeRangeForChart === 'today'; // Always include today when viewing today
          const shouldInclude = isWeekday && (!isToday || isMarketClosed || includeTodayRegardless);
          
          if (shouldInclude) {
            const current = tradeDayMap.get(dateKey) || 0;
            tradeDayMap.set(dateKey, current + agg.netPnl);
          }
        }
      });

      const tradingDays = tradeDayMap.size;
      
      if (tradingDays > 0) {
        // Calculate current average (all complete trading days)
        const totalPnL = Array.from(tradeDayMap.values()).reduce((sum, pnl) => sum + pnl, 0);
        const currentAverage = totalPnL / tradingDays;

        // Calculate previous average (exclude most recent day) for trend
        let previousAverage: number | null = null;
        if (tradingDays > 1) {
          const sortedDates = Array.from(tradeDayMap.keys()).sort();
          const mostRecentDate = sortedDates[sortedDates.length - 1];
          const previousTotal = totalPnL - (tradeDayMap.get(mostRecentDate) || 0);
          previousAverage = previousTotal / (tradingDays - 1);
        }

        // Determine trend
        let trend: 'up' | 'down' | null = null;
        if (previousAverage !== null) {
          trend = currentAverage >= previousAverage ? 'up' : 'down';
        }

        dailyAverage = {
          average: currentAverage,
          tradingDays,
          trend,
        };
      }
    }

    return {
      todayPnL,
      allTimePnL,
      bestTrade,
      worstTrade,
      bestStock,
      worstStock,
      dailyAverage,
      isShowingFallback: lastTradingDayInfo.shouldFallback,
      displayDateLabel: lastTradingDayInfo.lastTradingDateLabel,
    };
  }, [effectiveTodayAggregates, tradeAggregates, selectedPeriodAggregates, timeRangeForChart, lastTradingDayInfo]);

  // Helper function to format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Helper function to format percentage
  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Animation variants for staggered card animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Calculate P&L for selected period to determine border color
  const selectedPeriodPnL = useMemo(() => {
    if (timeRangeForChart === 'all') {
      return tradeAggregates.reduce((sum, agg) => sum + agg.netPnl, 0);
    } else if (timeRangeForChart === 'today') {
      return todayTradeAggregates.reduce((sum, agg) => sum + agg.netPnl, 0);
    } else {
      // For other periods, check if data is still loading to avoid calculating from empty array
      if (isLoadingSelectedPeriod) {
        return null; // Return null during loading to prevent incorrect zero P&L
      }
      return selectedPeriodAggregates.reduce((sum, agg) => sum + agg.netPnl, 0);
    }
  }, [timeRangeForChart, tradeAggregates, todayTradeAggregates, selectedPeriodAggregates, isLoadingSelectedPeriod]);

  // Determine border color based on selected period P&L
  const borderColorClass = selectedPeriodPnL === null
    ? 'border-gray-600' // Use neutral color during loading
    : selectedPeriodPnL >= 0 
      ? 'border-rh-green' 
      : 'border-rh-red';

  return (
    <div>
      {/* Annie Mode Phase 8 (PR #152) — banner + standings widget at the top */}
      <OpenPositionBanner />
      <ModeStandingsWidget />

      {/* Portfolio Value Header */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(totalValue)}
          </h1>
          <RegimeBadge
            regime={regimeData?.regime ?? null}
            probability={regimeData?.probability ?? 0}
            tradingDate={regimeData?.tradingDate ?? null}
            observationCount={regimeData?.observationCount ?? 0}
            insufficient={regimeData?.insufficient ?? true}
            isLoading={isLoadingRegime}
            regimeSince={regimeData?.regimeSince}
            previousRegime={regimeData?.previousRegime}
          />
        </div>
        
        {/* Period P&L Display - Robinhood Style */}
        {!isLoadingAggregates && selectedPeriodPnL !== null && (
          <motion.div 
            className="mb-6 space-y-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            {/* Selected Period P&L */}
            <div className={`flex items-center gap-2 text-base sm:text-lg font-semibold ${
              selectedPeriodPnL >= 0 ? 'text-rh-green' : 'text-rh-red'
            }`}>
              {/* Arrow */}
              {selectedPeriodPnL >= 0 ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              
              {/* Dollar Amount */}
              <span>{formatCurrency(Math.abs(selectedPeriodPnL))}</span>
              
              {/* Percentage */}
              <span>
                ({(() => {
                  const initialInvestment = totalValue - selectedPeriodPnL;
                  const percentReturn = initialInvestment > 0 
                    ? (selectedPeriodPnL / initialInvestment) * 100 
                    : 0;
                  return formatPercent(percentReturn);
                })()})
              </span>
              
              {/* Days Trading */}
              <span className="text-gray-500 dark:text-gray-400 font-normal">
                {pnlMetrics.dailyAverage.tradingDays} {pnlMetrics.dailyAverage.tradingDays === 1 ? 'Day' : 'Days'} Trading
              </span>
            </div>

            {/* Today's P&L */}
            {!isLoadingTodayAggregates && (
              <div className={`flex items-center gap-2 text-base sm:text-lg font-semibold ${
                pnlMetrics.todayPnL >= 0 ? 'text-rh-green' : 'text-rh-red'
              }`}>
                {/* Arrow */}
                {pnlMetrics.todayPnL >= 0 ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}

                {/* Dollar Amount */}
                <span>{formatCurrency(Math.abs(pnlMetrics.todayPnL))}</span>

                {/* Percentage */}
                <span>
                  ({(() => {
                    // Calculate today's percentage based on starting balance
                    const startingBalance = totalValue - pnlMetrics.todayPnL;
                    const percentReturn = startingBalance > 0
                      ? (pnlMetrics.todayPnL / startingBalance) * 100
                      : 0;
                    return formatPercent(percentReturn);
                  })()})
                </span>

                {/* Label */}
                <span className="text-gray-500 dark:text-gray-400 font-normal">
                  {pnlMetrics.displayDateLabel}
                </span>
              </div>
            )}

            {/* Circuit Breaker Progress Bar */}
            {!isLoadingCircuitBreaker && circuitBreakerStatus && circuitBreakerStatus.startOfDayBalance && (
              <CircuitBreakerBar
                dailyPnL={circuitBreakerStatus.dailyPnl || 0}
                startOfDayBalance={circuitBreakerStatus.startOfDayBalance}
                thresholdPct={circuitBreakerStatus.dailyLossLimitPct}
                isTriggered={circuitBreakerStatus.isTriggered}
                globalDailyPnl={circuitBreakerStatus.globalDailyPnl || 0}
                dailyProfitLimit={circuitBreakerStatus.dailyProfitLimit || 0}
              />
            )}

            {/* Profit Target Progress Indicator */}
            {!isLoadingCircuitBreaker && circuitBreakerStatus && circuitBreakerStatus.dailyProfitLimit > 0 && (
              <ProfitTargetIndicator
                dailyProfitLimit={circuitBreakerStatus.dailyProfitLimit}
                globalDailyPnl={circuitBreakerStatus.globalDailyPnl || 0}
                isTriggered={circuitBreakerStatus.isTriggered}
              />
            )}
          </motion.div>
        )}

        {/* Chart */}
        <div className="mb-4">
          <ProfitOverTimeChart
            accountFilter=""
            tickerFilter=""
            algorithmFilter=""
            timeRange={timeRangeForChart}
            regimeHistory={regimeHistory || []}
            regimePnL={regimePnL || []}
          />
        </div>

        {/* Time Period Selector */}
        <div className={`flex items-center gap-2 border-t pt-4 ${borderColorClass}`}>
          {[
            { label: '1D', value: '1d' },
            { label: '1W', value: '1w' },
            { label: '1M', value: '1m' },
            { label: '3M', value: '3m' },
            { label: 'YTD', value: 'ytd' },
            { label: '1Y', value: '1y' },
            { label: 'ALL', value: 'all' },
          ].map((period) => (
            <button
              key={period.value}
              onClick={() => setSelectedPeriod(period.value)}
              className={`text-xs font-normal transition-all duration-200 px-3 py-1.5 rounded-full ${
                selectedPeriod === period.value
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* P&L Stats Row */}
      <motion.div 
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mb-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Best Trade Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            🏆 Best Trade
          </div>
          {(timeRangeForChart === 'all' ? isLoadingAggregates : timeRangeForChart === 'today' ? isLoadingTodayAggregates : isLoadingSelectedPeriod) ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : pnlMetrics.bestTrade ? (
            <div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {pnlMetrics.bestTrade.ticker}
              </div>
              <div className={`flex items-center text-sm font-semibold ${
                pnlMetrics.bestTrade.pnl >= 0 ? 'text-rh-green' : 'text-rh-red'
              }`}>
                {pnlMetrics.bestTrade.pnl >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(pnlMetrics.bestTrade.pnl))}
              </div>
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>

        {/* Best Stock Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            📈 Best Stock
          </div>
          {(timeRangeForChart === 'all' ? isLoadingAggregates : timeRangeForChart === 'today' ? isLoadingTodayAggregates : isLoadingSelectedPeriod) ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : pnlMetrics.bestStock ? (
            <div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {pnlMetrics.bestStock.ticker}
              </div>
              <div className={`flex items-center text-sm font-semibold ${
                pnlMetrics.bestStock.pnl >= 0 ? 'text-rh-green' : 'text-rh-red'
              }`}>
                {pnlMetrics.bestStock.pnl >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(pnlMetrics.bestStock.pnl))}
              </div>
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>

        {/* Worst Trade Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            💩 Worst Trade
          </div>
          {(timeRangeForChart === 'all' ? isLoadingAggregates : timeRangeForChart === 'today' ? isLoadingTodayAggregates : isLoadingSelectedPeriod) ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : pnlMetrics.worstTrade ? (
            <div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {pnlMetrics.worstTrade.ticker}
              </div>
              <div className={`flex items-center text-sm font-semibold ${
                pnlMetrics.worstTrade.pnl >= 0 ? 'text-rh-green' : 'text-rh-red'
              }`}>
                {pnlMetrics.worstTrade.pnl >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(pnlMetrics.worstTrade.pnl))}
              </div>
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>

        {/* Worst Stock Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            📉 Worst Stock
          </div>
          {(timeRangeForChart === 'all' ? isLoadingAggregates : timeRangeForChart === 'today' ? isLoadingTodayAggregates : isLoadingSelectedPeriod) ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : pnlMetrics.worstStock ? (
            <div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {pnlMetrics.worstStock.ticker}
              </div>
              <div className={`flex items-center text-sm font-semibold ${
                pnlMetrics.worstStock.pnl >= 0 ? 'text-rh-green' : 'text-rh-red'
              }`}>
                {pnlMetrics.worstStock.pnl >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(pnlMetrics.worstStock.pnl))}
              </div>
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>

        {/* Daily Profit Average Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ 
            scale: 1.02,
            y: -2,
            transition: { duration: 0.2 }
          }}
          whileTap={{ scale: 0.98 }}
          title={pnlMetrics.dailyAverage.tradingDays > 0 ? `Based on ${pnlMetrics.dailyAverage.tradingDays} day${pnlMetrics.dailyAverage.tradingDays !== 1 ? 's' : ''} of trading` : 'No trading days yet'}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            🎯 Avg/Day ($100)
          </div>
          {(isLoadingAggregates || (timeRangeForChart !== 'all' && timeRangeForChart !== 'today' && isLoadingSelectedPeriod)) ? (
            <div className="flex items-center space-x-2">
              <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
            </div>
          ) : pnlMetrics.dailyAverage.tradingDays > 0 ? (
            <div className="flex items-center space-x-2">
              <div className={`text-xl sm:text-2xl font-bold ${
                pnlMetrics.dailyAverage.average >= 100 ? 'text-rh-green' : 'text-rh-red'
              }`}>
                ${Math.floor(pnlMetrics.dailyAverage.average).toLocaleString('en-US')}
              </div>
              {pnlMetrics.dailyAverage.trend && (
                <div className={pnlMetrics.dailyAverage.trend === 'up' ? 'text-rh-green' : 'text-rh-red'}>
                  {pnlMetrics.dailyAverage.trend === 'up' ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">
              —
            </div>
          )}
        </motion.div>

        {/* Total P&L Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            🍆 Total P&L
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : (
            <div className={`text-xl sm:text-2xl font-bold ${
              algorithmPerformance.totalPnL >= 0 ? 'text-rh-green' : 'text-rh-red'
            }`}>
              {formatCurrency(algorithmPerformance.totalPnL)}
            </div>
          )}
        </motion.div>

        {/* Win Rate Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            💯 Win Rate
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {algorithmPerformance.winRate.toFixed(1)}%
            </div>
          )}
        </motion.div>

        {/* Win/Loss Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            🏅 Win/Loss
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-rh-green">
              {algorithmPerformance.wins.toLocaleString()}/{algorithmPerformance.losses.toLocaleString()}
            </div>
          )}
        </motion.div>

        {/* Total Trades Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            🔢 Total Trades
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {algorithmPerformance.totalTrades.toLocaleString()}
            </div>
          )}
        </motion.div>

        {/* Avg Trade Duration Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            ⏱️ Avg Duration
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : algorithmPerformance.avgDuration ? (
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {algorithmPerformance.avgDuration}
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>

        {/* Total Buy Volume Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            💰 Buy Volume
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : algorithmPerformance.totalBuyVolume > 0 ? (
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(algorithmPerformance.totalBuyVolume)}
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>

        {/* Total Sell Volume Card */}
        <motion.div 
          className="rh-card p-4 sm:p-6 rh-hover-shadow"
          variants={cardVariants}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            💵 Sell Volume
          </div>
          {isLoadingAlgorithmMetrics ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
          ) : algorithmPerformance.totalSellVolume > 0 ? (
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(algorithmPerformance.totalSellVolume)}
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold text-gray-400 dark:text-gray-600">—</div>
          )}
        </motion.div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <RecentActivity />
      </motion.div>

      {/* Holdings Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <HoldingsTable 
          accounts={accounts}
          positions={openAggregates.map(agg => {
            const account = accounts.find(a => a.id === agg.accountId);
            const quantity = agg.buyQtyTotal - agg.sellQtyTotal;
            const costBasis = agg.buyNotionalTotal;
            // Note: marketValue = costBasis since we don't have live prices
            // This shows cost basis only; live price integration would be a future enhancement
            const marketValue = costBasis;
            const gainLoss = 0; // No unrealized P&L without live prices
            const gainLossPercent = 0;
            
            return {
              symbol: agg.ticker,
              quantity,
              marketValue,
              costBasis,
              gainLoss,
              gainLossPercent,
              accountId: agg.accountId,
              accountName: account?.accountName || 'Unknown Account',
              mode: agg.algorithm,
            };
          })}
          isLoading={isLoadingAccounts || isLoadingOpenAggregates}
        />
      </motion.div>

    </div>
  );
}
